import type { Address } from 'viem';

import { queryClient } from 'clients/api/queryClient';
import FunctionKey from 'constants/functionKey';
import { useGetContractAddress } from 'hooks/useGetContractAddress';
import { type UseSendTransactionOptions, useSendTransaction } from 'hooks/useSendTransaction';
import { relativePositionManagerAbi } from 'libs/contracts';
import { VError } from 'libs/errors';
import type { SwapQuote } from 'types';

export type IncreaseTradePositionInput = {
  longVTokenAddress: Address;
  shortVTokenAddress: Address;
  additionalPrincipalMantissa: bigint;
  shortAmountMantissa: bigint;
  minLongAmountMantissa: bigint;
  swapQuote: SwapQuote;
};

type Options = UseSendTransactionOptions<IncreaseTradePositionInput>;

export const useIncreaseTradePosition = (options?: Partial<Options>) => {
  const { address: relativePositionManagerContractAddress } = useGetContractAddress({
    name: 'RelativePositionManager',
  });

  return useSendTransaction({
    fn: ({
      longVTokenAddress,
      shortVTokenAddress,
      additionalPrincipalMantissa,
      shortAmountMantissa,
      minLongAmountMantissa,
      swapQuote,
    }: IncreaseTradePositionInput) => {
      if (!relativePositionManagerContractAddress) {
        throw new VError({
          type: 'unexpected',
          code: 'somethingWentWrong',
        });
      }

      return {
        abi: relativePositionManagerAbi,
        address: relativePositionManagerContractAddress,
        functionName: 'scalePosition',
        args: [
          longVTokenAddress,
          shortVTokenAddress,
          additionalPrincipalMantissa,
          shortAmountMantissa,
          minLongAmountMantissa,
          swapQuote.callData,
        ],
      } as const;
    },
    onConfirmed: () => {
      queryClient.invalidateQueries({
        queryKey: [FunctionKey.GET_RAW_TRADE_POSITIONS],
      });

      queryClient.invalidateQueries({
        queryKey: [FunctionKey.GET_ACCOUNT_TRANSACTION_HISTORY],
      });
    },
    options,
  });
};
